"use client"

import { useQuery } from "@tanstack/react-query"
import type { RecipeVariants } from "@vanilla-extract/recipes"
import { clsx } from "clsx"

import type { Nullish, PropsWithClassName } from "~/@types/generics"
import type { SbRichtext } from "~/lib/storyblok/schemas/default/richtext"
import BadgeTag from "~/components/ui/BadgeTag"
import { Image, type ImageProps } from "~/components/ui/Image"
import InlineCta from "~/components/ui/InlineCta"
import { Link, type LinkProps } from "~/components/ui/Link"
import { isVideo, isVimeoVideoUrl } from "~/components/ui/PromoCard/utils"
import RichText from "~/components/ui/RichText"
import SquareCta from "~/components/ui/SquareCta"
import Video, { type TVideo } from "~/components/ui/Video"
import ClientVimeoVideoPlayer from "~/components/ui/VideoPlayer/VimeoVideoPlayer/index.client"
import { getVimeoVideoRatio } from "~/components/abstracts/VideoPlayer/utils/get-vimeo-data"

import { sprinkles } from "~/styles/sprinkles.css"
import { breakpoints } from "~/styles/variables/breakpoints"

import * as css from "./styles.css"

const IMAGE_SIZES = [{ breakpoint: breakpoints.md, ratio: 3 / 4 }, { ratio: 1 }]

type PromoCardRecipe = RecipeVariants<typeof css.wrapper>

export type PromoCardProps = {
  image: ImageProps
  isCta?: boolean
  layout: "single-card" | "banner"
  link?: Nullish<LinkProps>
  paragraph?: SbRichtext
  subtitle?: Nullish<string>
  tag?: Nullish<string>
  title?: Nullish<string>
  video?: Nullish<string | { desktop: TVideo; mobile: Nullish<TVideo> }>
} & PromoCardRecipe &
  Pick<ImageProps, "priority">

export function PromoCard({
  className,
  color,
  image,
  isCta,
  layout = "single-card",
  link,
  paragraph,
  position,
  priority,
  video,
  tag,
  title,
  subtitle,
}: PropsWithClassName<PromoCardProps>) {
  const enabled = isVimeoVideoUrl(video)

  const { data: videoRatio } = useQuery(
    [video, "ratio"],
    async () => {
      if (enabled) {
        return await getVimeoVideoRatio(video)
      }

      return null
    },
    {
      enabled,
    }
  )

  const isLarge = layout === "banner"

  return (
    <Link className={clsx(className, css.PromoCard)} {...link}>
      <div className={clsx(css.wrapper({ color, position }))}>
        {isVideo(video) ? (
          <>
            <Video
              className={clsx(css.video, video.mobile && css.hideOnMobile, { [css.isLarge]: isLarge })}
              {...video.desktop}
              fill
            />
            {video.mobile && (
              <Video className={clsx(css.video, css.hideOnTablet, { [css.isLarge]: isLarge })} {...video.mobile} fill />
            )}
          </>
        ) : (
          <Image
            className={clsx(css.image, { [css.isLarge]: isLarge })}
            imageClassName={clsx(css.img({ center: !image.focus }))}
            sizesFromBreakpoints={IMAGE_SIZES}
            fill
            {...image}
            priority={priority}
          />
        )}
        {isVimeoVideoUrl(video) && videoRatio && (
          <ClientVimeoVideoPlayer
            className={css.media}
            playerClassName={css.player}
            fill
            url={video}
            ratio={videoRatio}
            thumbnail={{ alt: image.alt, src: image.src }}
          />
        )}

        <div className={clsx(css.container)}>
          {tag && (
            <BadgeTag className={clsx(css.tag)} color={color === "white" ? "black" : "white"}>
              {tag}
            </BadgeTag>
          )}
          {title && <p className={clsx(css.title)}>{title}</p>}
          {subtitle && <p className={clsx(css.subtitle)}>{subtitle}</p>}
          <RichText className={clsx(css.paragraph)} render={paragraph} />
          {isCta && link?.children ? (
            <SquareCta
              className={sprinkles({ marginTop: 12 })}
              theme={color === "black" ? "backgroundBlack" : "backgroundWhite"}
              {...link}
              href={undefined}
            />
          ) : (
            link?.children && (
              <InlineCta
                size="small"
                withLine
                withHover={false}
                className={sprinkles({ marginTop: 12 })}
                color={color}
                {...link}
                href={undefined}
              />
            )
          )}
        </div>
      </div>
    </Link>
  )
}
